/**
 * payoutReceiptReport — build a self-contained HTML payment-receipt
 * document for a `payout_transactions` row and pop it into a new tab
 * via a Blob URL (consistent with the rest of the app's print flows).
 *
 * The receipt is the formal proof a commission payout was processed. It
 * snapshots the agent's bank info at the moment of payment so even if
 * the agent later updates their info, the audit trail stays accurate.
 */

const fmtUSD = (n) =>
  new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 2 }).format(Number(n || 0));

const escapeHtml = (s) => {
  if (s == null) return "";
  return String(s).replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;").replace(/'/g, "&#39;");
};

const TYPE_LABEL = {
  issuance: "Card Issuance",
  topup: "Top-up",
  cash_deposit: "Cash Deposit",
};

const METHOD_LABEL = { cash: "Cash", card: "Card" };

export function buildPayoutReceiptReport(payout) {
  const processedAt = payout?.processed_at ? new Date(payout.processed_at) : null;
  const processedDisplay = processedAt
    ? processedAt.toLocaleString("en-US", { dateStyle: "long", timeStyle: "short" })
    : "—";
  const bank = payout?.bank_snapshot || {};
  const items = Array.isArray(payout?.line_items) ? payout.line_items : [];

  const sortedItems = [...items].sort((a, b) => new Date(a.earned_at || 0) - new Date(b.earned_at || 0));

  const itemRows = sortedItems.length === 0
    ? `<tr><td colspan="5" class="small" style="text-align:center; padding:18px;">No commission line-items found.</td></tr>`
    : sortedItems.map((it) => {
        const d = it?.earned_at ? new Date(it.earned_at) : null;
        const dateStr = d ? d.toLocaleString("en-US", { dateStyle: "medium", timeStyle: "short" }) : "—";
        const type = TYPE_LABEL[it.transaction_type] || (it.transaction_type || "—").replace(/_/g, " ");
        const method = METHOD_LABEL[(it.payment_method || "cash").toLowerCase()] || "Cash";
        return `<tr>
          <td class="small">${escapeHtml(dateStr)}</td>
          <td>${escapeHtml(it.receiver_name || "—")}<div class="small">${escapeHtml(it.receiver_email || "")}</div></td>
          <td>${escapeHtml(type)} <span class="method-tag">· ${escapeHtml(method)}</span></td>
          <td class="num">${escapeHtml(fmtUSD(it.transaction_amount))}</td>
          <td class="num strong">${escapeHtml(fmtUSD(it.commission_amount))}</td>
        </tr>`;
      }).join("");

  const html = `<!doctype html><html lang="en"><head><meta charset="utf-8"/>
<title>Zcardon · Payment Receipt · ${escapeHtml(payout?.id || "")}</title>
<style>
  * { box-sizing: border-box; }
  body { font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Arial, sans-serif; color: #112318; background: #fff; margin: 0; padding: 32px 40px; }
  .doc { max-width: 1050px; margin: 0 auto; }
  .brand { font-size: 11px; letter-spacing: 0.3em; text-transform: uppercase; color: #4A5C50; }
  h1 { font-family: "Cormorant Garamond", "Times New Roman", Georgia, serif; font-weight: 500; font-size: 40px; margin: 4px 0 0; letter-spacing: -0.5px; }
  h2 { font-family: "Cormorant Garamond", Georgia, serif; font-weight: 500; font-size: 22px; margin: 28px 0 8px; border-bottom: 1px solid #E5E0D8; padding-bottom: 4px; }
  .sub { color: #4A5C50; font-size: 13px; margin-top: 6px; }
  .totals-strip { margin-top: 18px; background: #112318; color: #FBF9F6; border-radius: 14px; padding: 18px 22px; display: flex; align-items: center; justify-content: space-between; }
  .totals-strip .label { font-size: 11px; letter-spacing: 0.25em; text-transform: uppercase; color: #8BA888; }
  .totals-strip .amount { font-family: "Cormorant Garamond", Georgia, serif; font-size: 42px; line-height: 1; margin-top: 4px; }
  .totals-strip .status { background: #DCF7E8; color: #166434; font-size: 10px; letter-spacing: 0.2em; text-transform: uppercase; font-weight: 700; padding: 5px 10px; border-radius: 999px; }
  .grid-2 { display: grid; grid-template-columns: 1fr 1fr; gap: 20px; margin-top: 16px; }
  .panel { border: 1px solid #E5E0D8; border-radius: 12px; padding: 14px 16px; background: #FBF9F6; }
  .panel h3 { margin: 0 0 8px; font-family: "Cormorant Garamond", Georgia, serif; font-weight: 500; font-size: 18px; }
  .panel .row { display: flex; justify-content: space-between; align-items: baseline; padding: 4px 0; font-size: 12px; }
  .panel .row .k { color: #4A5C50; font-size: 10px; letter-spacing: 0.18em; text-transform: uppercase; }
  .panel .row .v { font-family: "SF Mono", Menlo, monospace; color: #112318; }
  .meta { margin-top: 14px; display: grid; grid-template-columns: repeat(3, 1fr); gap: 10px; }
  .meta .item { border: 1px solid #E5E0D8; padding: 10px 12px; border-radius: 8px; }
  .meta .label { font-size: 10px; letter-spacing: 0.2em; text-transform: uppercase; color: #4A5C50; }
  .meta .val { font-size: 14px; margin-top: 3px; font-family: "SF Mono", Menlo, monospace; }
  table { width: 100%; border-collapse: collapse; margin-top: 6px; font-size: 12px; }
  th, td { padding: 7px 8px; border-bottom: 1px solid #E5E0D8; text-align: left; vertical-align: top; }
  th { font-size: 10px; letter-spacing: 0.15em; text-transform: uppercase; color: #4A5C50; }
  td.num { text-align: right; font-variant-numeric: tabular-nums; font-family: "SF Mono", Menlo, monospace; }
  td.strong, .strong { font-weight: 600; }
  td.small, .small { font-size: 10px; color: #4A5C50; }
  .method-tag { font-size: 10px; color: #8C6B21; }
  .tx-total-row td { background: #FBF7EE; font-weight: 600; border-top: 2px solid #112318; }
  .signature-row { margin-top: 36px; display: grid; grid-template-columns: 1fr 1fr; gap: 40px; }
  .sig-block { border-top: 1px solid #112318; padding-top: 8px; font-size: 10px; color: #4A5C50; letter-spacing: 0.2em; text-transform: uppercase; }
  .footer { margin-top: 28px; font-size: 10px; color: #4A5C50; text-align: center; }
  .print-bar { position: fixed; top: 12px; right: 16px; display: flex; gap: 8px; }
  .print-bar button { background: #184A30; color: #fff; border: none; padding: 8px 14px; border-radius: 999px; font-size: 12px; cursor: pointer; font-family: inherit; }
  .print-bar button.secondary { background: #fff; color: #184A30; border: 1px solid #184A30; }
  @media print {
    .print-bar { display: none; }
    body { padding: 24px; }
  }
</style></head><body>
  <div class="print-bar">
    <button onclick="window.print()">Print receipt</button>
    <button class="secondary" onclick="window.close()">Close</button>
  </div>
  <div class="doc">
    <div class="brand">Zcardon · Commission payout receipt</div>
    <h1>Payment processed</h1>
    <div class="sub">Receipt #<span class="strong">${escapeHtml((payout?.id || "").slice(0, 8).toUpperCase())}</span> · Issued ${escapeHtml(processedDisplay)} by ${escapeHtml(payout?.processed_by || "—")}</div>

    <div class="totals-strip">
      <div>
        <div class="label">Amount paid</div>
        <div class="amount">${escapeHtml(fmtUSD(payout?.total_amount))}</div>
      </div>
      <div style="text-align: right;">
        <span class="status">✓ ${escapeHtml(payout?.status || "processed")}</span>
        <div class="sub" style="color:#8BA888; margin-top:6px;">${escapeHtml(payout?.row_count || items.length)} commission row${(payout?.row_count || items.length) === 1 ? "" : "s"} settled</div>
      </div>
    </div>

    <div class="grid-2">
      <div class="panel">
        <h3>Paid by</h3>
        <div class="row"><span class="k">Entity</span><span class="v strong">Zcardon, Inc.</span></div>
        <div class="row"><span class="k">Processed by</span><span class="v">${escapeHtml(payout?.processed_by || "—")}</span></div>
        <div class="row"><span class="k">Reference</span><span class="v">${escapeHtml(payout?.payment_ref || "—")}</span></div>
        ${payout?.note ? `<div class="row"><span class="k">Note</span><span class="v" style="text-align:right;max-width:60%;">${escapeHtml(payout.note)}</span></div>` : ""}
      </div>
      <div class="panel">
        <h3>Paid to</h3>
        <div class="row"><span class="k">Agent</span><span class="v strong">${escapeHtml(payout?.agent_full_name || "—")}</span></div>
        <div class="row"><span class="k">Agent code</span><span class="v">${escapeHtml(payout?.agent_code || "—")}</span></div>
        <div class="row"><span class="k">Email</span><span class="v">${escapeHtml(payout?.agent_email || "—")}</span></div>
        ${payout?.agent_phone ? `<div class="row"><span class="k">Phone</span><span class="v">${escapeHtml(payout.agent_phone)}</span></div>` : ""}
        ${payout?.agent_address ? `<div class="row"><span class="k">Address</span><span class="v" style="text-align:right;max-width:60%;">${escapeHtml(payout.agent_address)}</span></div>` : ""}
      </div>
    </div>

    <h2>Bank routing</h2>
    <p class="sub" style="margin-top:0;">Snapshot taken at the moment payment was processed. Later edits to the agent's bank info don't affect this receipt.</p>
    <div class="meta">
      <div class="item"><div class="label">Bank</div><div class="val">${escapeHtml(bank.bank_name || "—")}</div></div>
      <div class="item"><div class="label">Account holder</div><div class="val">${escapeHtml(bank.bank_account_holder || "—")}</div></div>
      <div class="item"><div class="label">Account ••••</div><div class="val">${escapeHtml(bank.account_last4 || "—")}</div></div>
      <div class="item"><div class="label">Routing ••••</div><div class="val">${escapeHtml(bank.routing_last4 || "—")}</div></div>
      <div class="item"><div class="label">SWIFT / BIC</div><div class="val">${escapeHtml(bank.swift_code || "—")}</div></div>
      <div class="item"><div class="label">IBAN</div><div class="val">${bank.iban_last4 ? `${escapeHtml(bank.iban_country)} •••• ${escapeHtml(bank.iban_last4)}` : "—"}</div></div>
    </div>

    <h2>Commission line items</h2>
    <table data-testid="payout-receipt-line-items">
      <thead>
        <tr>
          <th style="width: 20%;">Earned</th>
          <th style="width: 28%;">Receiver</th>
          <th style="width: 22%;">Type</th>
          <th style="width: 15%; text-align: right;">Tx amount</th>
          <th style="width: 15%; text-align: right;">Commission</th>
        </tr>
      </thead>
      <tbody>
        ${itemRows}
        ${sortedItems.length > 0 ? `<tr class="tx-total-row">
          <td colspan="3" class="strong">Total commission paid</td>
          <td class="num">—</td>
          <td class="num strong">${escapeHtml(fmtUSD(payout?.total_amount))}</td>
        </tr>` : ""}
      </tbody>
    </table>

    <div class="signature-row">
      <div class="sig-block">Zcardon signature</div>
      <div class="sig-block">Recipient signature</div>
    </div>

    <div class="footer">
      This receipt is a formal record that the listed commission amounts were processed for payout to the agent's bank account on file.<br />
      Receipt #${escapeHtml(payout?.id || "")} · Generated ${escapeHtml(new Date().toLocaleString())} · Zcardon · Confidential.
    </div>
  </div>
</body></html>`;

  const blob = new Blob([html], { type: "text/html" });
  const url = URL.createObjectURL(blob);
  const w = window.open(url, "_blank", "noopener,noreferrer,width=1100,height=900");
  // Revoke later to avoid leaking the blob URL.
  setTimeout(() => URL.revokeObjectURL(url), 120_000);
  if (!w) {
    // Popup blocked — return a fallback URL the caller can render as a link.
    return { ok: false, blocked: true, url };
  }
  return { ok: true };
}

/**
 * Helper: fetch the formal payout_transaction by id and render its
 * printable receipt. Callers in the admin payouts dashboard use this
 * so they don't have to deal with the fetch step themselves.
 */
export async function openPayoutReceipt(api, payoutId) {
  if (!payoutId) return { ok: false };
  const r = await api.get(`/admin/payout-transactions/${payoutId}`);
  return buildPayoutReceiptReport(r.data);
}
